/**
 * UnassignedFirefliesNotes
 *
 * Displays Fireflies.ai transcripts that were ingested but could not be
 * confidently classified to a project. Assignment goes through the shared
 * backend action so RAG indexing and portfolio reporting sync run consistently.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  Calendar,
  FileText,
  Flame,
  Loader2,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useFirefliesIntegration, type UnassignedFirefliesNote } from '@/hooks/useFirefliesIntegration';
import { useBulkSelection, bulkAssignNotes, bulkDeleteNotes } from '@/lib/bulkNoteActions';

interface ProjectOption {
  id: string;
  name: string;
}

export function UnassignedFirefliesNotes() {
  const { user } = useAuth();
  const { unassignedNotes, assignToProject } = useFirefliesIntegration();
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [bulkActioning, setBulkActioning] = useState(false);
  const [bulkProjectId, setBulkProjectId] = useState('');

  const {
    selectedIds,
    selectedCount,
    toggle,
    toggleAll,
    clearAll,
    isAllSelected,
    isSomeSelected,
    isSelected,
  } = useBulkSelection(unassignedNotes);

  useEffect(() => {
    if (!user?.uid) return;

    const loadProjects = async () => {
      try {
        const q = query(
          collection(db, 'projects'),
          where('team', 'array-contains', user.uid)
        );
        const snap = await getDocs(q);
        const opts: ProjectOption[] = snap.docs.map((d) => ({
          id: d.id,
          name: d.data().name || 'Untitled Project',
        }));
        opts.sort((a, b) => a.name.localeCompare(b.name));
        setProjects(opts);
      } catch (error) {
        console.error('[UnassignedFirefliesNotes] Failed to load projects:', error);
      }
    };

    loadProjects();
  }, [user?.uid]);

  if (unassignedNotes.length === 0) {
    return null;
  }

  const handleSingleAssign = async (noteId: string, projectId: string) => {
    setAssigning(noteId);
    try {
      await assignToProject(noteId, projectId);
    } finally {
      setAssigning(null);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkProjectId || selectedCount === 0) return;
    setBulkActioning(true);
    try {
      await bulkAssignNotes(Array.from(selectedIds), bulkProjectId);
      clearAll();
      setBulkProjectId('');
    } finally {
      setBulkActioning(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCount === 0) return;
    setBulkActioning(true);
    try {
      await bulkDeleteNotes(Array.from(selectedIds));
      clearAll();
    } finally {
      setBulkActioning(false);
    }
  };

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <Checkbox
            checked={isAllSelected}
            ref={undefined}
            onCheckedChange={toggleAll}
            aria-label="Select all Fireflies notes"
            className={isSomeSelected ? 'data-[state=checked]:bg-primary [&:not([data-state=checked])]:bg-primary/30' : ''}
          />
          <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Flame className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg">Unassigned Fireflies Notes</CardTitle>
            <CardDescription>
              {selectedCount > 0
                ? `${selectedCount} of ${unassignedNotes.length} selected`
                : "These Fireflies transcripts couldn't be automatically matched to a project"
              }
            </CardDescription>
          </div>
          <Badge variant="secondary">{unassignedNotes.length}</Badge>
        </div>
      </CardHeader>

      <div className="relative">
        <CardContent className="space-y-3 max-h-[600px] overflow-y-auto pb-20">
          <AnimatePresence mode="popLayout">
            {unassignedNotes.map((note) => (
              <motion.div
                key={note.id}
                layout
                initial={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.2 }}
              >
                <NoteCard
                  note={note}
                  projects={projects}
                  assigning={assigning === note.id}
                  selected={isSelected(note.id)}
                  onToggle={() => toggle(note.id)}
                  onAssign={(projectId) => handleSingleAssign(note.id, projectId)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </CardContent>

        <AnimatePresence>
          {selectedCount > 0 && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="sticky bottom-0 left-0 right-0 border-t bg-card/95 backdrop-blur-sm px-6 py-3"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium tabular-nums">
                  {selectedCount} selected
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  className="h-7 px-2 text-xs text-muted-foreground"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear
                </Button>

                <div className="flex-1" />

                <Select value={bulkProjectId} onValueChange={setBulkProjectId}>
                  <SelectTrigger className="w-[200px] h-8 text-xs">
                    <SelectValue placeholder="Select project..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="text-xs">
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  className="h-8 text-xs"
                  disabled={!bulkProjectId || bulkActioning}
                  onClick={handleBulkAssign}
                >
                  {bulkActioning ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : null}
                  Assign {selectedCount}
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="h-8 text-xs"
                      disabled={bulkActioning}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {selectedCount} Fireflies note{selectedCount > 1 ? 's' : ''}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete the selected Fireflies transcripts.
                        This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleBulkDelete}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete {selectedCount} note{selectedCount > 1 ? 's' : ''}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Card>
  );
}

function NoteCard({
  note,
  projects,
  assigning,
  selected,
  onToggle,
  onAssign,
}: {
  note: UnassignedFirefliesNote;
  projects: ProjectOption[];
  assigning: boolean;
  selected: boolean;
  onToggle: () => void;
  onAssign: (projectId: string) => void;
}) {
  const [selectedProject, setSelectedProject] = useState('');

  return (
    <div
      className={`border rounded-lg p-4 space-y-3 transition-colors ${
        selected
          ? 'border-primary/50 bg-primary/5'
          : 'border-border hover:border-border/80'
      }`}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          aria-label={`Select ${note.name}`}
          className="mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm text-foreground truncate">{note.name}</h4>
          <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {note.meetingDate.toLocaleDateString()}
            </span>
            {note.attendees.length > 0 && (
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {note.attendees.length} attendee{note.attendees.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      {note.metadata.firefliesSummary && (
        <p className="text-xs text-muted-foreground line-clamp-2 pl-8">
          <FileText className="h-3 w-3 inline mr-1" />
          {note.metadata.firefliesSummary}
        </p>
      )}

      {note.classificationConfidence > 0 && (
        <div className="text-xs text-muted-foreground pl-8">
          AI confidence: {Math.round(note.classificationConfidence * 100)}%
          {note.classificationReasoning && ` - ${note.classificationReasoning}`}
        </div>
      )}

      {note.metadata.firefliesActionItems && note.metadata.firefliesActionItems.length > 0 && (
        <div className="text-xs text-muted-foreground pl-8">
          <span className="font-medium">{note.metadata.firefliesActionItems.length} action item(s)</span>
        </div>
      )}

      {note.metadata.firefliesTopics && note.metadata.firefliesTopics.length > 0 && (
        <div className="text-xs text-muted-foreground pl-8">
          Topics: {note.metadata.firefliesTopics.slice(0, 5).join(', ')}
        </div>
      )}

      <div className="flex gap-2 pt-1 pl-8">
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="flex-1 h-8 text-xs">
            <SelectValue placeholder="Assign to project..." />
          </SelectTrigger>
          <SelectContent>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id} className="text-xs">
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          className="h-8 text-xs"
          disabled={!selectedProject || assigning}
          onClick={() => onAssign(selectedProject)}
        >
          {assigning ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Assign'}
        </Button>
      </div>
    </div>
  );
}
